/**
 * Tenth Hour — cart actions
 *
 * Quick-add from the cart page, and closing the drawer when the customer
 * chooses to keep shopping. Delegated so it survives the theme re-rendering
 * the cart after every quantity change.
 *
 * Every network path is bounded by a timeout and restores the button in a
 * finally block: a stalled request must never leave a control dead on screen.
 */
(() => {
  if (window.__thCartActions) return;
  window.__thCartActions = true;

  const REQUEST_TIMEOUT = 12000;
  const busy = new WeakSet();

  /** fetch with a hard timeout, so a stalled mobile connection can't hang the UI */
  async function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  }

  function cartUrl(path) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + path;
  }

  document.addEventListener('click', (event) => {
    // event.target can be a non-Element (text node, document) — closest() would throw
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Continue shopping — inside the drawer, just close it.
    const keepShopping = target.closest('[data-th-close-drawer]');
    if (keepShopping) {
      const dialog = keepShopping.closest('dialog');
      if (dialog && typeof dialog.close === 'function') {
        event.preventDefault();
        dialog.close();
      }
      return;
    }

    const swatch = target.closest('.th-cartx__swatch');
    if (!swatch || swatch.disabled || busy.has(swatch)) return;

    event.preventDefault();
    quickAdd(swatch);
  });

  async function quickAdd(swatch) {
    const wrap = swatch.closest('[data-th-quickadd]');
    const msg = wrap ? wrap.querySelector('[data-th-msg]') : null;
    const id = Number(swatch.dataset.variant);
    if (!id) return;

    busy.add(swatch);
    swatch.classList.add('is-adding');
    if (msg) msg.hidden = true;

    let reloading = false;
    try {
      const res = await fetchWithTimeout(cartUrl('/cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add that pair.');

      // Let the theme re-render the cart. A reload is the reliable path here.
      reloading = true;
      window.location.reload();
    } catch (err) {
      const aborted = err && err.name === 'AbortError';
      if (msg) {
        msg.textContent = aborted
          ? 'That took too long. Check your connection and try again.'
          : err.message || 'Could not add that pair.';
        msg.hidden = false;
      }
    } finally {
      // Leave the control disabled only while the page is actually navigating away.
      if (!reloading) {
        swatch.classList.remove('is-adding');
        busy.delete(swatch);
      }
    }
  }
})();
